import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import type { KeyboardEvent } from "react";
import React, { useState } from "react";

import { GetCallPlanDetailQuery, InteractionType } from "@/graphql/graphql";
import { formatEnumLabel } from "@/utils/format";

import {
  activityDialogAuthorSx,
  activityDialogDescriptionBoxPlanDetailSx,
  activityDialogDescriptionTextSx,
  activityDialogParagraphSx,
} from "../CreateCallPlanModal/ActivityDialog/ActivityDialog.styles";
import QuestionBody from "../CreateCallPlanModal/ActivityDialog/QuestionBody";
import {
  viewCallPlanActivityChevronIconSx,
  viewCallPlanActivityChevronWrapSx,
  viewCallPlanActivityHeaderSx,
  viewCallPlanActivityHeaderTextColumnSx,
  viewCallPlanActivitySectionLabelSx,
  viewCallPlanActivityTitleSx,
  viewCallPlanInteractionSectionSx,
  viewCallPlanOtherHeadingRowSx,
  viewCallPlanOtherHeadingTitleColumnSx,
  viewCallPlanOtherInteractionStackSx,
  viewCallPlanOtherInteractionTitleSx,
  viewCallPlanRemoveStepIconSx,
} from "./ViewCallPlanModal.styles";

export type CallPlanDetailInteraction = NonNullable<
  NonNullable<GetCallPlanDetailQuery["callPlan"]["interactions"]>[number]
>;

export type ActivityInteractionDetail = Extract<
  CallPlanDetailInteraction,
  { __typename?: "ActivityInteraction" }
>;

const INTERACTION_TYPES_NOT_REMOVABLE: InteractionType[] = [
  InteractionType.Greeting,
  InteractionType.Activity,
  InteractionType.ScamWarning,
  InteractionType.Transition,
];

const isRemovableInteraction = (interaction: CallPlanDetailInteraction): boolean =>
  !INTERACTION_TYPES_NOT_REMOVABLE.includes(interaction.type);

const activitySectionToggleKeyActivate =
  (callbackFn: () => void) => (e: KeyboardEvent<Element>) => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }

    e.preventDefault();
    callbackFn();
  };

export const ActivitySection = ({ interaction }: { interaction: ActivityInteractionDetail }) => {
  const [activityExpanded, setActivityExpanded] = useState(false);

  const { firstName, lastName } = interaction.createdBy;
  const authorLine = `${firstName} ${lastName}`.trim();

  const description = interaction.description;
  const showDescription = Boolean(description?.trim());

  const toggleActivityExpanded = () => setActivityExpanded((v) => !v);

  return (
    <Box sx={viewCallPlanInteractionSectionSx}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={activityExpanded}
        aria-label={
          activityExpanded ? "Collapse activity details" : `Expand activity: ${interaction.title}`
        }
        onClick={toggleActivityExpanded}
        onKeyDown={activitySectionToggleKeyActivate(toggleActivityExpanded)}
        sx={viewCallPlanActivityHeaderSx(activityExpanded)}
      >
        <Box component="span" sx={viewCallPlanActivityChevronWrapSx} aria-hidden>
          {activityExpanded ? (
            <KeyboardArrowUpIcon sx={viewCallPlanActivityChevronIconSx} />
          ) : (
            <KeyboardArrowRightIcon sx={viewCallPlanActivityChevronIconSx} />
          )}
        </Box>
        <Box sx={viewCallPlanActivityHeaderTextColumnSx}>
          <Typography component="span" sx={viewCallPlanActivitySectionLabelSx}>
            Activity
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={viewCallPlanActivityTitleSx(activityExpanded)}
          >
            {interaction.title}
          </Typography>
          {authorLine && (
            <Typography
              component="p"
              variant="body2"
              sx={activityDialogAuthorSx}
              aria-label={`Activity author: ${authorLine}`}
            >
              {authorLine}
            </Typography>
          )}
        </Box>
      </Box>
      {activityExpanded && (
        <>
          {showDescription && (
            <Box sx={activityDialogDescriptionBoxPlanDetailSx}>
              <Typography component="p" sx={activityDialogDescriptionTextSx}>
                {description}
              </Typography>
            </Box>
          )}
          <QuestionBody steps={interaction.contents} layout="planDetail" />
        </>
      )}
    </Box>
  );
};

export const InteractionSection: React.FC<{
  interaction: CallPlanDetailInteraction;
  interactionRemoveBlockedReason: string | null;
  onStageRemoveInteraction?: (interactionId: number) => void;
  blockInteractionStaging?: boolean;
}> = ({
  interaction,
  interactionRemoveBlockedReason,
  onStageRemoveInteraction,
  blockInteractionStaging,
}) => {
  if (interaction.__typename === "ActivityInteraction") {
    return <ActivitySection interaction={interaction} />;
  }

  if (interaction.__typename !== "OtherInteraction") {
    return null;
  }

  const showRemoveControl = isRemovableInteraction(interaction);
  const body = interaction.contentText?.trim();

  const removeButton = (
    <IconButton
      size="small"
      color="error"
      aria-label={
        interactionRemoveBlockedReason
          ? interactionRemoveBlockedReason
          : `Remove ${formatEnumLabel(interaction.type)} step`
      }
      disabled={!!interactionRemoveBlockedReason || blockInteractionStaging}
      onClick={
        interactionRemoveBlockedReason || !onStageRemoveInteraction || blockInteractionStaging
          ? undefined
          : () => onStageRemoveInteraction(interaction.id)
      }
      sx={viewCallPlanRemoveStepIconSx}
    >
      <DeleteOutlineRoundedIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box sx={viewCallPlanInteractionSectionSx}>
      <Box sx={viewCallPlanOtherInteractionStackSx}>
        <Box sx={viewCallPlanOtherHeadingRowSx}>
          {showRemoveControl && (
            <Tooltip
              title={
                interactionRemoveBlockedReason
                  ? interactionRemoveBlockedReason
                  : "Stage this step for removal. Choose Save changes to apply."
              }
            >
              <span>{removeButton}</span>
            </Tooltip>
          )}
          <Box sx={viewCallPlanOtherHeadingTitleColumnSx(showRemoveControl)}>
            <Typography component="h3" sx={viewCallPlanOtherInteractionTitleSx}>
              {formatEnumLabel(interaction.type)}
            </Typography>
          </Box>
        </Box>
        {body ? (
          <Typography component="p" sx={activityDialogParagraphSx}>
            {body}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No instruction text for this step.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
